import { apiUrl } from '../../lib/build';
import type { Shot, TicketCard, TicketCounts, TicketKind, TicketStatus, TicketThread } from './types';

export type Got<T> = { ok: true; body: T } | { ok: false; status: number; error: string };

async function call<T>(path: string, init?: RequestInit): Promise<Got<T>> {
  try {
    const res = await fetch(apiUrl(path), {
      credentials: 'same-origin',
      ...init,
      headers: init?.body ? { 'content-type': 'application/json' } : undefined,
    });
    if (!res.ok) {
      let error = res.status === 429 ? 'слишком часто — подождите немного'
        : res.status === 401 ? 'войдите в аккаунт и повторите отправку'
        : res.status === 413 ? 'скрины слишком тяжёлые — уберите пару'
        : res.status >= 500 ? (init?.method === 'POST'
          ? 'сервер временно недоступен — текст и скрины сохранены в форме, попробуйте ещё раз'
          : 'сервер временно недоступен — попробуйте ещё раз')
        : 'не отправилось, попробуйте ещё раз';
      try {
        const body = (await res.json()) as { detail?: unknown };
        if (typeof body.detail === 'string' && body.detail) error = body.detail;
      } catch {
        // ответ без JSON — остаётся общая фраза
      }
      return { ok: false, status: res.status, error };
    }
    return { ok: true, body: (await res.json()) as T };
  } catch {
    return { ok: false, status: 0, error: 'нет связи — проверьте интернет' };
  }
}

const files = (shots: Shot[]): { data: string; w: number; h: number }[] =>
  shots.map((shot) => ({ data: shot.data, w: shot.w, h: shot.h }));

export const fileUrl = (id: string): string => apiUrl(`/v1/me/tickets/files/${encodeURIComponent(id)}`);

export const myTickets = (): Promise<Got<{ tickets: TicketCard[]; unread: number }>> => call('/v1/me/tickets');

export const myUnread = (): Promise<Got<{ unread: number }>> => call('/v1/me/tickets/unread');

export const myThread = (id: number): Promise<Got<TicketThread>> => call(`/v1/me/tickets/${id}`);

export function openTicket(input: {
  kind: TicketKind;
  text: string;
  shots: Shot[];
  page: string;
  screen: string;
}): Promise<Got<{ id: number; tickets: TicketCard[] }>> {
  return call('/v1/me/tickets', {
    method: 'POST',
    body: JSON.stringify({ kind: input.kind, text: input.text, files: files(input.shots), page: input.page, screen: input.screen }),
  });
}

export function replyMine(id: number, text: string, shots: Shot[], close = false): Promise<Got<TicketThread>> {
  return call(`/v1/me/tickets/${id}/reply`, {
    method: 'POST',
    body: JSON.stringify({ text, files: files(shots), close }),
  });
}

export type DeskList = { tickets: TicketCard[]; counts: TicketCounts };

export const deskTickets = (status = ''): Promise<Got<DeskList>> =>
  call(`/v1/me/owner/tickets${status ? `?status=${status}` : ''}`);

export const deskCount = (): Promise<Got<TicketCounts>> => call('/v1/me/owner/tickets/count');

export const deskThread = (id: number): Promise<Got<TicketThread>> => call(`/v1/me/owner/tickets/${id}`);

export function deskReply(id: number, text: string, shots: Shot[], status: TicketStatus | null): Promise<Got<TicketThread>> {
  return call(`/v1/me/owner/tickets/${id}/reply`, {
    method: 'POST',
    body: JSON.stringify({ text, files: files(shots), status }),
  });
}
